// ============================================================================
// OPTIMIZED APP COMPONENT - PERFORMANCE ENHANCED
// frontend/src/App.tsx
// 🆕 WITH TIMESHEET REPORT ROUTE + HR PIN DASHBOARD ROUTING + REACT QUERY
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './config/queryClient'; // 🆕 Import queryClient
import Login from './components/auth/Login';
import Dashboard from './components/dashboard/Dashboard';
import EngineerDashboard from './components/engineer/EngineerDashboard';
import ApproverDashboard from './components/approver/ApproverDashboard';
import { HrPinDashboard } from './components/hr-pin'; // 🆕 HR PIN Dashboard from new folder
import EmployeeTimesheetReport from './components/dashboard/pages/EmployeeTimesheetReport';
import { AuthUser, authService } from './services/auth.service';
import './App.css';

const App: React.FC = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((authenticatedUser) => {
      setUser(authenticatedUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLoginSuccess = (authenticatedUser: AuthUser) => {
    console.log('Login successful:', {
      name: authenticatedUser.employeeName || authenticatedUser.name,
      isPinAuth: authenticatedUser.isPinAuth,
      userType: authenticatedUser.userType,
      isHrManager: authenticatedUser.isHrManager,
      isAssistantManager: authenticatedUser.isAssistantManager,
      isAccountingManager: authenticatedUser.isAccountingManager, // Added
      isApprover: authenticatedUser.isApprover,
      isAdmin: authenticatedUser.isAdmin
    });
    setUser(authenticatedUser);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setUser(null);
    } catch {
      // Silent error handling
    }
  };

  // Memoized dashboard component selection
  const dashboardComponent = useMemo(() => {
    if (!user) return null;

    // =========================================================================
    // 🆕 PRIORITY 1: Check for PIN-based HR Manager/Assistant Manager/Accounting Manager FIRST
    // This must be checked BEFORE approver because HR managers may also have
    // hasOvertimeApprovalAccess = true, but they should go to HR PIN Dashboard
    // =========================================================================
    if (user.isPinAuth && (user.isHrManager || user.isAssistantManager || user.isAccountingManager || 
        user.userType === 'hr_manager' || user.userType === 'assistant_manager' || user.userType === 'accounting_manager')) {
      console.log('Routing to HR PIN Dashboard for:', user.employeeName || user.name, 'as', user.userType);
      return <HrPinDashboard user={user} onLogout={handleLogout} />;
    }

    // =========================================================================
    // PRIORITY 2: Check for admin (full access to main dashboard)
    // =========================================================================
    if (user.isAdmin || user.userType === 'admin') {
      console.log('Routing to Admin Dashboard for:', user.employeeName || user.name);
      return <Dashboard user={user} onLogout={handleLogout} />;
    }

    // =========================================================================
    // PRIORITY 3: Check for HR Manager/Assistant Manager/Accounting Manager (non-PIN login)
    // These users logged in via email/password get full Dashboard access
    // =========================================================================
    if (user.isHrManager || user.isAssistantManager || user.isAccountingManager || 
        user.userType === 'hr_manager' || user.userType === 'assistant_manager' || user.userType === 'accounting_manager') {
      console.log('Routing to Full Dashboard for HR/Accounting:', user.employeeName || user.name, 'as', user.userType);
      return <Dashboard user={user} onLogout={handleLogout} />;
    }

    // =========================================================================
    // PRIORITY 4: Check for approver (overtime approval access)
    // =========================================================================
    if (user.isApprover || user.userType === 'approver') {
      console.log('Routing to Approver Dashboard for:', user.employeeName || user.name);
      return <ApproverDashboard user={user} onLogout={handleLogout} />;
    }
    
    // =========================================================================
    // PRIORITY 5: Check for engineer
    // =========================================================================
    if (user.isEngineer || user.userType === 'engineer') {
      console.log('Routing to Engineer Dashboard for:', user.employeeName || user.name);
      return <EngineerDashboard user={user} onLogout={handleLogout} />;
    }
    
    // =========================================================================
    // DEFAULT: Employee portal (under development)
    // =========================================================================
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f0f4f8',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <div style={{
          background: 'white',
          padding: '3rem',
          borderRadius: '1rem',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '400px'
        }}>
          <h2 style={{ color: '#1f2937', marginBottom: '1rem' }}>
            Welcome {user.employeeName || user.name}!
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
            Employee portal is under development. Please contact your administrator for access.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{
              background: '#f3f4f6',
              padding: '1rem',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              color: '#374151'
            }}>
              User Type: {user.userType}<br/>
              Department: {user.department || 'Not assigned'}<br/>
              Employee ID: {user.employeeNo || user.employeeNumber || 'Not assigned'}
            </div>
            <button 
              onClick={handleLogout}
              style={{
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }, [user]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Phoenician-HR</p>
      </div>
    );
  }

  // ============================================================================
  // 🆕 WRAP EVERYTHING WITH QueryClientProvider
  // ============================================================================
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="App">
          <Routes>
            {/* 🆕 Timesheet Report Route - Accessible without authentication for print */}
            <Route path="/timesheet-report" element={<EmployeeTimesheetReport />} />
            
            {/* Main Application Routes */}
            <Route path="/*" element={
              !user ? (
                <Login onLoginSuccess={handleLoginSuccess} />
              ) : (
                dashboardComponent
              )
            } />
          </Routes>
        </div>
      </Router>
      
      {/* 🆕 React Query DevTools - Only in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools 
          initialIsOpen={false}
        />
      )}
    </QueryClientProvider>
  );
};

export default App;